<template>
  <div style="width: 97%">
    <a-modal
      :dialog-style="{
        top: '25%',
      }"
      :title="$t('chat.startNewConversationWithParent')"
      :visible="visibleAddParnet"
      width="70%"
      @cancel="handleCancel"
    >
      <div class="parent-selection-container">
        <!-- Search Bar -->
        <div class="search-container">
          <a-input-search
            v-model="searchQuery"
            :placeholder="$t('chat.searchParents')"
            size="large"
          >
            <a-icon slot="prefix" type="search" />
          </a-input-search>
        </div>

        <!-- Parent List -->
        <div class="parent-list">
          <div
            v-for="parent in filteredParentList"
            :key="parent._id"
            class="parent-family-item"
          >
            <!-- Family Header (Collapsible) -->
            <div
              class="family-header"
              @click="toggleFamilyCollapse(parent._id)"
            >
              <div class="family-info">
                <a-avatar
                  :src="
                    parent.photo
                      ? settings.resourcesUrl.replace('FILE_PATH', parent.photo)
                      : undefined
                  "
                  :size="40"
                  icon="user"
                />
                <span class="family-name">
                  {{ getFamilyName(parent) }}
                </span>
              </div>
              <div class="family-actions">
                <a-icon
                  :type="collapsedFamilies.includes(parent._id) ? 'down' : 'up'"
                  class="collapse-icon"
                />
              </div>
            </div>

            <!-- Collapsible Parent Members -->
            <div
              v-show="!collapsedFamilies.includes(parent._id)"
              class="parent-members"
            >
              <!-- Father -->
              <div
                v-if="parent.father && parent.father.login"
                class="parent-member-item"
                :class="{
                  disabled: checkIfParentAlreadyAdded(parent._id, 'father'),
                  inactive: parent.father.status === 'inactive',
                  selected: isParentSelected(parent._id, 'father'),
                }"
                @click="
                  toggleParentSelection(parent._id, 'father', parent.father)
                "
              >
                <div class="member-info">
                  <a-avatar
                    :src="
                      parent.father.photo
                        ? settings.resourcesUrl.replace(
                            'FILE_PATH',
                            parent.father.photo
                          )
                        : parent.photo
                        ? settings.resourcesUrl.replace(
                            'FILE_PATH',
                            parent.photo
                          )
                        : undefined
                    "
                    :size="32"
                    icon="user"
                  />
                  <div class="member-details">
                    <span class="member-name">
                      {{ parent.father.firstName }} {{ parent.father.lastName }}
                    </span>
                    <span class="member-type">{{
                      getParentTypeTranslation("father")
                    }}</span>
                  </div>
                </div>
                <div class="member-status">
                  <a-icon
                    v-if="parent.father.status === 'inactive'"
                    type="stop"
                    theme="filled"
                    class="status-icon inactive"
                    :title="$t('chat.inactiveParent')"
                  />
                  <a-icon
                    v-else-if="checkIfParentAlreadyAdded(parent._id, 'father')"
                    type="check-circle"
                    theme="filled"
                    class="status-icon added"
                    :title="$t('chat.alreadyAdded')"
                  />
                  <a-icon
                    v-else
                    type="plus-circle"
                    theme="filled"
                    class="status-icon available"
                    :title="$t('chat.availableToAdd')"
                  />
                </div>
              </div>

              <!-- Mother -->
              <div
                v-if="parent.mother && parent.mother.login"
                class="parent-member-item"
                :class="{
                  disabled: checkIfParentAlreadyAdded(parent._id, 'mother'),
                  inactive: parent.mother.status === 'inactive',
                  selected: isParentSelected(parent._id, 'mother'),
                }"
                @click="
                  toggleParentSelection(parent._id, 'mother', parent.mother)
                "
              >
                <div class="member-info">
                  <a-avatar
                    :src="
                      parent.mother.photo
                        ? settings.resourcesUrl.replace(
                            'FILE_PATH',
                            parent.mother.photo
                          )
                        : parent.photo
                        ? settings.resourcesUrl.replace(
                            'FILE_PATH',
                            parent.photo
                          )
                        : undefined
                    "
                    :size="32"
                    icon="user"
                  />
                  <div class="member-details">
                    <span class="member-name">
                      {{ parent.mother.firstName }} {{ parent.mother.lastName }}
                    </span>
                    <span class="member-type">{{
                      getParentTypeTranslation("mother")
                    }}</span>
                  </div>
                </div>
                <div class="member-status">
                  <a-icon
                    v-if="parent.mother.status === 'inactive'"
                    type="stop"
                    theme="filled"
                    class="status-icon inactive"
                    :title="$t('chat.inactiveParent')"
                  />
                  <a-icon
                    v-else-if="checkIfParentAlreadyAdded(parent._id, 'mother')"
                    type="check-circle"
                    theme="filled"
                    class="status-icon added"
                    :title="$t('chat.alreadyAdded')"
                  />
                  <a-icon
                    v-else
                    type="plus-circle"
                    theme="filled"
                    class="status-icon available"
                    :title="$t('chat.availableToAdd')"
                  />
                </div>
              </div>

              <!-- Guardian -->
              <div
                v-if="parent.guardian && parent.guardian.login"
                class="parent-member-item"
                :class="{
                  disabled: checkIfParentAlreadyAdded(parent._id, 'guardian'),
                  inactive: parent.guardian.status === 'inactive',
                  selected: isParentSelected(parent._id, 'guardian'),
                }"
                @click="
                  toggleParentSelection(parent._id, 'guardian', parent.guardian)
                "
              >
                <div class="member-info">
                  <a-avatar
                    :src="
                      parent.guardian.photo
                        ? settings.resourcesUrl.replace(
                            'FILE_PATH',
                            parent.guardian.photo
                          )
                        : parent.photo
                        ? settings.resourcesUrl.replace(
                            'FILE_PATH',
                            parent.photo
                          )
                        : undefined
                    "
                    :size="32"
                    icon="user"
                  />
                  <div class="member-details">
                    <span class="member-name">
                      {{ parent.guardian.firstName }}
                      {{ parent.guardian.lastName }}
                    </span>
                    <span class="member-type">{{
                      getParentTypeTranslation("guardian")
                    }}</span>
                  </div>
                </div>
                <div class="member-status">
                  <a-icon
                    v-if="parent.guardian.status === 'inactive'"
                    type="stop"
                    theme="filled"
                    class="status-icon inactive"
                    :title="$t('chat.inactiveParent')"
                  />
                  <a-icon
                    v-else-if="
                      checkIfParentAlreadyAdded(parent._id, 'guardian')
                    "
                    type="check-circle"
                    theme="filled"
                    class="status-icon added"
                    :title="$t('chat.alreadyAdded')"
                  />
                  <a-icon
                    v-else
                    type="plus-circle"
                    theme="filled"
                    class="status-icon available"
                    :title="$t('chat.availableToAdd')"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- No Results Message -->
        <div v-if="filteredParentList.length === 0" class="no-results">
          <a-empty :description="$t('chat.noParentsFound')" />
        </div>
      </div>
      <template slot="footer">
        <a-button key="back" @click="cancelAddParent">
          {{ $t("action.annuler") }}
        </a-button>
        <a-button
          key="submit"
          type="primary"
          :disabled="selectedParents.length === 0"
          @click="handleAddParents"
        >
          {{ $t("chat.addParent") }} ({{ selectedParents.length }})
        </a-button>
      </template>
    </a-modal>
    <vue-advanced-chat
      height="calc(100vh - 120px)"
      class="mr-10"
      :current-user-id="currentUserId"
      :rooms="JSON.stringify(rooms)"
      :text-messages="JSON.stringify(textMessages)"
      show-reaction-emojis="false"
      show-audio="false"
      :show-add-room="roomsLoaded ? 'true' : 'false'"
      :messages="JSON.stringify(messages)"
      :message-actions="JSON.stringify(messageActions)"
      :messages-loaded="messagesLoaded"
      :rooms-loaded="roomsLoaded"
      @send-message="sendMessage($event.detail[0])"
      @fetch-messages="fetchMessages($event.detail[0])"
      @delete-message="deleteMessageHandler($event.detail[0])"
      @open-file="openFile($event.detail[0])"
      @add-room="addNewParent($event.detail[0])"
      load-first-room="true"
      show-new-messages-divider="false"
    >
      <chat-message-video-slot
        v-for="msg in messagesWithChatVideo"
        :key="'chat-vid-slot-' + messageSlotKey(msg)"
        :slot="'message_' + messageSlotKey(msg)"
        :message="msg"
        :current-user-id="currentUserId"
        :resources-url="settings.resourcesUrl"
        :room-users="selectedRoom ? selectedRoom.users || [] : []"
        @delete="handleVideoSlotDelete"
      />
      <div
        slot="room-options"
        v-if="selectedRoom && getParentIdFromRoomId(selectedRoom.roomId)"
      >
        <div style="width: 450px; display: flex; justify-content: right">
          <PaymentStatus
            :currentParent="getParentIdFromRoomId(selectedRoom.roomId)"
            v-if="verifyAccess(['all', 'payment_status'])"
          />
          <ParentDetails
            :currentParent="getParentIdFromRoomId(selectedRoom.roomId)"
            v-if="verifyAccess(['all', 'parent_details'])"
          />
        </div>
      </div>
    </vue-advanced-chat>
  </div>
</template>

<script>
/* eslint-disable */

import { register } from "vue-advanced-chat";
import apiClient from "@/services/axios";
import { mapState } from "vuex";
import PaymentStatus from "@/components/status/paymentStatus.vue";
import ParentDetails from "./components/parentDetails.vue";
import ChatMessageVideoSlot from "./components/ChatMessageVideoSlot.vue";
import moment from "moment";
import { uploadFilesToS3 } from "@/services/uploadService";
import store from "store";
import tokenStorage from "@/services/tokenStorage";
register();
export default {
  components: {
    PaymentStatus,
    ParentDetails,
    ChatMessageVideoSlot,
  },
  data() {
    return {
      selectedParents: [],
      visibleAddParnet: false,
      parentList: [],
      roomsLoaded: false,
      currentUserId: "0",
      rooms: [],
      messages: [],
      messagesLoaded: false,
      selectedRoom: null,
      unseenMessages: [],
      messageActions: [
        {
          name: "deleteMessage",
          title: this.$t("chat.deleteMessage"),
          onlyMe: true,
        },
      ],
      textMessages: {
        ROOMS_EMPTY: this.$t("chat.ROOMS_EMPTY"),
        ROOM_EMPTY: this.$t("chat.ROOM_EMPTY"),
        NEW_MESSAGES: this.$t("chat.NEW_MESSAGES"),
        MESSAGE_DELETED: this.$t("chat.MESSAGE_DELETED"),
        MESSAGES_EMPTY: this.$t("chat.MESSAGES_EMPTY"),
        CONVERSATION_STARTED: this.$t("chat.CONVERSATION_STARTED"),
        TYPE_MESSAGE: this.$t("chat.TYPE_MESSAGE"),
        SEARCH: this.$t("chat.SEARCH"),
        IS_ONLINE: this.$t("chat.IS_ONLINE"),
        LAST_SEEN: this.$t("chat.LAST_SEEN"),
      },
      connectedParents: [],
      connectedTeachers: [],
      searchQuery: "",
      collapsedFamilies: [],
      videoModal: { visible: false, video: null },
    };
  },

  computed: {
    ...mapState(["settings", "user"]),
    schoolDetails() {
      return (
        this.user.building.filter(
          (el) => el.dbName == this.settings.activeBuilding
        )[0] || {}
      );
    },
    filteredParentList() {
      if (!this.searchQuery) {
        return this.parentList;
      }
      return this.parentList.filter((parent) =>
        this.getFamilyName(parent)
          .toLowerCase()
          .includes(this.searchQuery.toLowerCase())
      );
    },
    messagesWithChatVideo() {
      return this.messages.filter((m) => {
        if (!m || m.system || m.deleted) return false;
        const files = m.files || [];
        return files.some((f) => this.fileNeedsCustomVideoPlayer(f));
      });
    },
  },

  beforeDestroy() {
    this.$socket.client.off("parentSeen");
    this.$socket.client.off("chat-status");
    this.$socket.client.off("adminMessage");
  },

  async created() {
    await this.fetchAndUpdateRooms();
    this.refreshConnectionStatus();
    this.$socket.client.on(
      "connectionList",
      ({ admins, parents, teachers }) => {
        this.connectedParents = parents || [];
        this.connectedTeachers = teachers || [];
        this.updateRoomsOnlineStatus();
      }
    );
    this.$socket.client.on("deletedMessage", ({ messageId, parentId }) => {
      if (this.selectedRoom.users[1].familyId == parentId) {
        const messages = [...this.messages];
        const message = messages.find(
          (message) => message.messageId == messageId
        );
        if (message) {
          message.deleted = true;
          this.messages = messages;
        }
      }
      const updatedRooms = this.rooms.map((room) => {
        if (
          room.users[1].familyId == parentId &&
          room.lastMessage &&
          room.lastMessage._id == messageId
        ) {
          const updatedRoom = { ...room };
          updatedRoom.lastMessage = {
            ...updatedRoom.lastMessage,
            content: "deleted msg",
            deleted: true,
          };
          return updatedRoom;
        }
        return room;
      });
      this.rooms = updatedRooms;
    });

    this.$socket.client.on("parentSeen", ({ parentId, parentType }) => {
      const messages = [...this.messages];
      if (this.selectedRoom.users[1].loginId == parentId)
        messages.map((message) => {
          message.seen = true;
          return message;
        });
      this.messages = messages;

      // Update rooms with new reference to trigger reactivity - only for specific parent type
      const updatedRooms = this.rooms.map((room) => {
        if (
          room.users[1].familyId == parentId &&
          room.users[1].parentType == parentType
        ) {
          const updatedRoom = { ...room };
          if (updatedRoom.lastMessage) {
            updatedRoom.lastMessage = {
              ...updatedRoom.lastMessage,
              seen: true,
            };
          }
          return updatedRoom;
        }
        return room;
      });
      this.rooms = updatedRooms;
    });

    // listen to new messages from parent or admin (saved in db)
    this.$socket.client.on(
      "adminMessage",
      async ({
        sentBy, // admin or parent
        parentId,
        message,
        messageId,
        date,
        files,
        videos,
        temp_id,
        parentType, // parent type (father, mother, guardian)
        loginId,
      }) => {
        const room = this.rooms.find(
          (room) => room.users[1].familyId == parentId
        );
        if (!room) await this.fetchAndUpdateRooms(); // create new room for parent

        const messages = [...this.messages];

        // Build regular file objects
        const message_files = [];
        if (files && files.length > 0) {
          files.forEach((file) => {
            const splited = file.split(".");
            const splitedName = splited[splited.length - 2].split("--")[1];
            const isAudio = this.isAudioFile(file);
            message_files.push({
              name: splitedName,
              type: isAudio
                ? this.getAudioMimeType(file)
                : splited[splited.length - 1],
              url: this.settings.resourcesUrl.replace("FILE_PATH", file),
              preview: this.settings.resourcesUrl.replace("FILE_PATH", file),
              audio: isAudio,
              duration: isAudio ? 0 : undefined,
            });
          });
        }

        const all_files = [
          ...message_files,
          ...this.mapVideosToFiles(videos),
        ];

        if (sentBy == "admin") {
          // Check if this message belongs to the currently selected room
          const targetRoomId = `${parentId}-${parentType}`;
          const isCurrentRoom =
            this.selectedRoom && this.selectedRoom.roomId === targetRoomId;

          if (isCurrentRoom) {
            const msg = messages.find(
              (message) => message.messageId == temp_id
            );
            if (!msg) {
              messages.push({
                _id: messageId,
                messageId: messageId,
                content: message,
                saved: true,
                seen: false,
                senderId: this.currentUserId,
                timestamp: new Date(date).toString().substring(16, 21),
                date: moment(date).format("DD/MM/YYYY"),
                files: all_files,
                parentType: parentType,
                isLegacy:
                  typeof parentType === "undefined" || parentType === null,
              });
            } else {
              const savedId =
                messageId != null ? String(messageId) : msg.messageId;
              msg.messageId = savedId;
              msg.saved = true;
              msg.seen = false;
              msg.files =
                all_files && all_files.length > 0 ? all_files : msg.files || [];
              msg.content = message;
              msg.parentType = parentType;
              msg.isLegacy =
                typeof parentType === "undefined" || parentType === null;
            }
          }
        } else {
          // Check if this message belongs to the currently selected room
          const targetRoomId = `${parentId}-${parentType}`;
          const isCurrentRoom =
            this.selectedRoom && this.selectedRoom.roomId === targetRoomId;

          if (!isCurrentRoom) {
            const specificRoom = this.rooms.find(
              (room) =>
                room.users[1].familyId == parentId &&
                room.users[1].parentType == parentType
            );
            if (specificRoom) {
              this.roomNewMessages(specificRoom.roomId, 1);
            }
          } else {
            messages.push({
              _id: messageId,
              messageId,
              content: message,
              saved: true,
              seen: false,
              senderId: parentId,
              timestamp: new Date(date).toString().substring(16, 21),
              date: moment(date).format("DD/MM/YYYY"),
              files: all_files,
              parentType: parentType,
              isLegacy:
                typeof parentType === "undefined" || parentType === null,
            });
            this.$socket.client.emit("adminSeen", {
              parentId,
            });
          }
        }

        // Update the last message for the specific room (not all family members)
        const updatedRooms = this.rooms.map((m) => {
          // Find the room that matches both familyId and the specific parent type
          if (
            m.users[1].familyId == parentId &&
            m.users[1].parentType == parentType
          ) {
            const updatedRoom = { ...m };
            updatedRoom.lastMessage = {
              _id: messageId,
              content: message,
              senderId: sentBy == "parent" ? parentId : "0",
              username:
                sentBy == "parent" ? m.users[1].username : "Administration",
              timestamp: new Date(date).toString().substring(16, 21),
              saved: true,
              seen: false,
              deleted: false,
            };
            return updatedRoom;
          }
          return m;
        });

        // Update rooms with new reference to trigger reactivity
        this.rooms = updatedRooms;

        // Only update messages if they were modified (i.e., the message belongs to current room)
        const targetRoomId = `${parentId}-${parentType}`;
        const isCurrentRoom =
          this.selectedRoom && this.selectedRoom.roomId === targetRoomId;

        if (isCurrentRoom) {
          this.messages = messages;
        }
      }
    );

    // listening for online/offline status
    this.$socket.client.on("chat-status", ({ type, parentId }) => {
      if (type == "parentOffline")
        this.rooms.map((room) => {
          if (room.users[1].loginId == parentId)
            room.users[1].status.state = "false";
          return room;
        });
      else if (type == "parentOnline")
        this.rooms.map((room) => {
          if (room.users[1].loginId == parentId)
            room.users[1].status.state = "online";
          return room;
        });
      else console.log("chat-status", type);
    });

    // get all parent list (to use in add parent modal)
    apiClient
      .post("/parents/filter", {
        query: { status: "active" },
        aggregation: [
          {
            $project: {
              father: {
                firstName: 1,
                lastName: 1,
                login: 1,
                status: 1,
              },
              mother: {
                firstName: 1,
                lastName: 1,
                login: 1,
                status: 1,
              },
              guardian: {
                firstName: 1,
                lastName: 1,
                login: 1,
                status: 1,
              },
              _id: 1,
              photo: 1,
            },
          },
        ],
      })
      .then(({ data }) => {
        this.parentList = data;
      });
  },

  methods: {
    getParentTypeTranslation(parentType) {
      const translationMap = {
        father: "father",
        mother: "mother",
        guardian: "guardian",
      };
      return this.$t("profil." + translationMap[parentType]);
    },
    getRoomContext(room) {
      const targetUser = room?.users?.find((u) => u && u._id !== "0") || {};
      const roomId = room?.roomId || "";
      const roomIdParts = roomId.split("-");
      const fallbackFamilyId = roomIdParts[0] || null;
      const fallbackParentType = roomIdParts.length > 1 ? roomIdParts[1] : null;
      return {
        familyId: targetUser.familyId || fallbackFamilyId,
        parentType: targetUser.parentType || fallbackParentType || null,
      };
    },

    handleCancel(e) {
      this.visibleAddParnet = false;
    },
    deleteMessageHandler({ roomId, message }) {
      this.$socket.client.emit("deleteMessage", {
        parentId: this.selectedRoom.users[1].familyId,
        loginId: this.selectedRoom.users[1].loginId,
        messageId: message.messageId,
      });
    },
    handleVideoSlotDelete({ message }) {
      this.deleteMessageHandler({
        roomId: this.selectedRoom && this.selectedRoom.roomId,
        message,
      });
    },
    messageSlotKey(msg) {
      if (!msg) return "";
      const id = msg._id != null ? msg._id : msg.messageId;
      return id != null ? String(id) : "";
    },
    fileNeedsCustomVideoPlayer(file) {
      if (!file) return false;
      if (file.isChatVideo) return true;
      const t = (file.type || "").toLowerCase();
      const n = (file.name || "").toLowerCase();
      if (t.includes("video")) return true;
      return [".mp4", ".webm", ".mov", ".mkv"].some((ext) => n.endsWith(ext));
    },
    async fetchAndUpdateRooms() {
      let unseenMessages = [];
      await apiClient.get("/chat/admin/unseenMessages").then((res) => {
        unseenMessages = res.data;
      });

      const rooms = [];
      const createdRoomIds = new Set(); // Track room IDs being created in this session
      let update = false;

      // get all parent chats
      await apiClient
        .get("/chat/admin/parentChats")
        .then((res) => {
          res.data.map((parent) => {
            // Get the parent data based on parentType from backend
            let parentData = null;
            let parentType = parent.parentType;

            if (parentType === "father" && parent.father) {
              parentData = parent.father;
            } else if (parentType === "mother" && parent.mother) {
              parentData = parent.mother;
            } else if (parentType === "guardian" && parent.guardian) {
              parentData = parent.guardian;
            } else if (parentType === null) {
              // For legacy messages, create rooms for all parent types that have login
              const parentTypes = [
                { type: "father", data: parent.father },
                { type: "mother", data: parent.mother },
                { type: "guardian", data: parent.guardian },
              ];

              parentTypes.forEach(({ type, data }) => {
                if (data && data.login) {
                  const roomId = `${parent._id}-${type}`;
                  const name = `${data.firstName} ${
                    data.lastName
                  } (${this.getParentTypeTranslation(type)})`;
                  const isOnline = this.isParentOnline(data.login);
                  const status = isOnline
                    ? { state: "online" }
                    : { state: "offline" };

                  // Use individual parent's photo if available, otherwise fall back to family photo
                  const photo = data.photo
                    ? this.settings.resourcesUrl.replace(
                        "FILE_PATH",
                        data.photo
                      )
                    : parent.photo
                    ? this.settings.resourcesUrl.replace(
                        "FILE_PATH",
                        parent.photo
                      )
                    : "-";

                  const lastMessage = {
                    _id: parent.lastMessage?._id || "",
                    content: parent.lastMessage?.deleted
                      ? "deleted msg"
                      : parent.lastMessage?.message || "",
                    senderId:
                      parent.lastMessage?.sentBy == "parent"
                        ? parent.lastMessage?.parentId
                        : "0",
                    username: name,
                    saved: true,
                    timestamp: parent.lastMessage?.createdAt
                      ? new Date(parent.lastMessage.createdAt)
                          .toString()
                          .substring(16, 21)
                      : "",
                    seen: parent.lastMessage?.seen || false,
                    deleted: parent.lastMessage?.deleted || false,
                  };

                  if (
                    !this.rooms.find((room) => room.roomId == roomId) &&
                    !createdRoomIds.has(roomId)
                  ) {
                    createdRoomIds.add(roomId);
                    update = true;
                    rooms.push({
                      roomId: roomId,
                      roomName: name,
                      unreadCount:
                        unseenMessages.find((p) => p._id == roomId)?.count || 0,
                      avatar: photo,
                      lastMessage,
                      users: [
                        {
                          _id: "0",
                          username: "Adminstration",
                          status: {
                            state: "online",
                            lastChanged: "today, 14:30",
                          },
                        },
                        {
                          _id: roomId,
                          username: name,
                          status: status,
                          loginId: data.login,
                          parentType: type,
                          familyId: parent._id,
                        },
                      ],
                    });
                  }
                }
              });
              return; // Skip the rest of the loop for legacy
            }

            // Only create room if parent type exists and has login (for non-legacy)
            if (parentData && parentData.login) {
              const roomId = `${parent._id}-${parentType}`;
              const name = `${parentData.firstName} ${
                parentData.lastName
              } (${this.getParentTypeTranslation(parentType)})`;
              const isOnline = this.isParentOnline(parentData.login);
              const status = isOnline
                ? { state: "online" }
                : { state: "offline" };

              // Use individual parent's photo if available, otherwise fall back to family photo
              const photo = parentData.photo
                ? this.settings.resourcesUrl.replace(
                    "FILE_PATH",
                    parentData.photo
                  )
                : parent.photo
                ? this.settings.resourcesUrl.replace("FILE_PATH", parent.photo)
                : "-";

              const lastMessage = {
                _id: parent.lastMessage?._id || "",
                content: parent.lastMessage?.deleted
                  ? "deleted msg"
                  : parent.lastMessage?.message || "",
                senderId:
                  parent.lastMessage?.sentBy == "parent"
                    ? parent.lastMessage?.parentId
                    : "0",
                username: name,
                saved: true,
                timestamp: parent.lastMessage?.createdAt
                  ? new Date(parent.lastMessage.createdAt)
                      .toString()
                      .substring(16, 21)
                  : "",
                seen: parent.lastMessage?.seen || false,
                deleted: parent.lastMessage?.deleted || false,
              };

              if (
                !this.rooms.find((room) => room.roomId == roomId) &&
                !createdRoomIds.has(roomId)
              ) {
                createdRoomIds.add(roomId);
                update = true;
                rooms.push({
                  roomId: roomId,
                  roomName: name,
                  unreadCount:
                    unseenMessages.find((p) => p._id == roomId)?.count || 0,
                  avatar: photo,
                  lastMessage,
                  users: [
                    {
                      _id: "0",
                      username: "Adminstration",
                      status: {
                        state: "online",
                        lastChanged: "today, 14:30",
                      },
                    },
                    {
                      _id: roomId,
                      username: name,
                      status: status,
                      loginId: parentData.login, // Save login ID for socket usage
                      parentType: parentType,
                      familyId: parent._id,
                    },
                  ],
                });
              }
            }
          });

          this.rooms.push(...rooms);
          if (this.rooms.length > 0 && !update) {
            this.selectedRoom = this.rooms[0];
            const { familyId } = this.getRoomContext(this.selectedRoom);
            this.$socket.client.emit("adminSeen", {
              parentId: familyId || this.selectedRoom.roomId,
            });
          }
          this.updateRoomsOnlineStatus();
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          this.roomsLoaded = true;
        });
    },
    roomNewMessages(roomId, count) {
      const rooms = [...this.rooms];
      rooms.map((room) => {
        if (room.roomId == roomId) {
          room.unreadCount += count;
        }
        return room;
      });
      this.rooms = rooms;
    },
    fetchMessages({ room, options = {} }) {
      this.selectedRoom = room;
      const messages = [];

      this.messagesLoaded = false;

      room.unreadCount = 0;
      this.rooms.map((m) => {
        if (m.roomId == room.roomId) {
          m.unreadCount = 0;
        }
        return m;
      });

      const { familyId, parentType } = this.getRoomContext(room);
      const roomKey = parentType ? `${familyId}-${parentType}` : familyId;
      apiClient
        .get("/chat/admin/messages/" + roomKey)
        .then((res) => {
          res.data.map((msg) => {
            const message_files = [];
            if (msg.files && msg.files.length > 0) {
              msg.files.forEach((file) => {
                const splited = file.split(".");
                const splitedName = splited[splited.length - 2].split("--")[1];
                const isAudio = this.isAudioFile(file);
                message_files.push({
                  name: splitedName,
                  type: isAudio
                    ? this.getAudioMimeType(file)
                    : splited[splited.length - 1],
                  url: this.settings.resourcesUrl.replace("FILE_PATH", file),
                  preview: this.settings.resourcesUrl.replace(
                    "FILE_PATH",
                    file
                  ),
                  audio: isAudio,
                  duration: isAudio ? 0 : undefined,
                });
              });
            }

            const all_files = [
              ...message_files,
              ...this.mapVideosToFiles(msg.videos),
            ];

            messages.push({
              _id: msg._id,
              messageId: msg._id,
              content: msg.message,
              senderId: msg.sentBy == "admin" ? "0" : msg.parent,
              timestamp: new Date(msg.createdAt).toString().substring(16, 21),
              date: moment(msg.createdAt).format("DD/MM/YYYY"),
              files: all_files,
              seen: msg.seen,
              saved: true,
              deleted: Boolean(msg.deleted),
              parentType: msg.parentType,
              isLegacy:
                typeof msg.parentType === "undefined" ||
                msg.parentType === null,
            });
          });

          // Insert a divider directly after the last legacy message (no parentType)
          let lastLegacyIndex = -1;
          for (let i = 0; i < res.data.length; i++) {
            const m = res.data[i];
            if (
              typeof m?.parentType === "undefined" ||
              m?.parentType === null
            ) {
              lastLegacyIndex = i;
            }
          }

          if (lastLegacyIndex >= 0) {
            const idx = Math.min(lastLegacyIndex, messages.length - 1);
            const dividerId = `system-legacy-divider-${room.roomId}`;
            if (!messages.find((m) => m?.messageId === dividerId)) {
              const dividerMessage = {
                _id: dividerId,
                messageId: dividerId,
                content:
                  "Les messages ci-dessus ont été envoyés lorsque les parents avaient un compte joint.",
                senderId: "system",
                system: true,
                saved: true,
                date: messages[idx]?.date,
              };
              messages.splice(lastLegacyIndex + 1, 0, dividerMessage);
            }
          }

          setTimeout(() => {
            this.messagesLoaded = true;
            this.messages = messages;

            this.$socket.client.emit("adminSeen", {
              parentId: familyId || room.roomId,
            });
          }, 100);
        })
        .catch((err) => {
          console.error(err);
        });
    },

    async sendMessage(message) {
      if (this.schoolDetails.pack.access.edManager["chat"] != "v") return;

      const VIDEO_TYPES = [
        "video/mp4", "video/webm", "video/mpeg",
        "video/quicktime", "video/x-msvideo", "video/x-matroska",
      ];
      const REGULAR_TYPES = [
        "image/jpeg", "image/jpg", "image/png",
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-powerpoint",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      ];

      const allFiles = message.files || [];
      const videoFiles = allFiles.filter((f) =>
        VIDEO_TYPES.includes((f.blob.originFileObj || f.blob).type)
      );
      const regularFiles = allFiles.filter((f) =>
        !VIDEO_TYPES.includes((f.blob.originFileObj || f.blob).type)
      );
      if (regularFiles.length > 15) {
        this.$message.error(this.$t("error.maxImage"));
        return;
      }
      let regularSize = 0;
      for (const file of regularFiles) {
        const raw = file.blob.originFileObj || file.blob;
        if (!REGULAR_TYPES.includes(raw.type)) {
          this.$message.error(
            this.$t("error.fileTypeNotAllowed") ||
              "Only images, PDF, and Office files are allowed"
          );
          return;
        }
        regularSize += raw.size;
      }
      if (regularSize / 1024 / 1024 > 50) {
        this.$message.error(this.$t("error.image50"));
        return;
      }

      if (videoFiles.length > 3) {
        this.$message.error(
          this.$t("error.maxVideos")
        );
        return;
      }
      for (const file of videoFiles) {
        const raw = file.blob.originFileObj || file.blob;
        if (raw.size > 500 * 1024 * 1024) {
          this.$message.error(
            this.$t("error.videoSize")
          );
          return;
        }
      }

      const temp_id = String(Math.floor(Date.now()));
      setTimeout(async () => {
        const messages = [...this.messages];
        const selectedRoom = this.rooms.find(
          (room) => room.roomId === message.roomId
        );
        const { familyId, parentType } = this.getRoomContext(selectedRoom);
        const loginId = selectedRoom?.users?.find(
          (user) => user._id !== "0"
        )?.loginId;

        // Optimistic chat videos: real blob preview + upload progress in VideoPlayer (message_* slot)
        const tempVideoFiles = videoFiles.map((vf, i) => {
          const raw = vf.blob.originFileObj || vf.blob;
          const localBlobUrl = URL.createObjectURL(raw);
          const originalName = String(vf.name || `video-${i + 1}`);
          const originalExt =
            (originalName.split(".").pop() || "").toLowerCase() || "mp4";
          const mimeType = (raw.type || "video/mp4").toLowerCase();
          const baseName = String(vf.name || `video-${i + 1}`).replace(
            /\.[^.]+$/,
            ""
          );
          return {
            name: `${baseName} (uploading…).${originalExt}`,
            type: mimeType,
            extension: originalExt,
            url: localBlobUrl,
            preview: localBlobUrl,
            isChatVideo: true,
            localBlobUrl,
            videoData: {
              status: "UPLOADING",
              originalKey: null,
              hlsUrl: null,
              thumbnailUrl: null,
              renditions: [],
              uploadProgress: 0,
            },
          };
        });

        messages.push({
          _id: temp_id,
          messageId: temp_id,
          content: message.content,
          senderId: this.currentUserId,
          timestamp: new Date().toString().substring(16, 21),
          date: moment().format("DD/MM/YYYY"),
          files: [
            ...(regularFiles.length > 0 ? this.formattedFiles(regularFiles) : []),
            ...tempVideoFiles,
          ],
          parentType: parentType || null,
          isLegacy: typeof parentType === "undefined" || parentType === null,
          tempMessageId: temp_id,
        });

        // Mark regular file thumbnails as uploading
        if (regularFiles.length > 0) {
          this.$nextTick(() => {
            const messageElements = document.querySelectorAll(".vac-message");
            messageElements.forEach((messageElement) => {
              const msgId =
                messageElement.getAttribute("data-message-id") ||
                messageElement.getAttribute("data-id");
              if (msgId === temp_id.toString()) {
                messageElement
                  .querySelectorAll(".vac-message-file")
                  .forEach((el) => {
                    el.classList.add("uploading");
                    el.style.setProperty("--upload-progress", "0%");
                  });
              }
            });
          });
        }

        this.messages = messages;

        this.rooms = this.rooms.map((m) => {
          if (m.roomId === message.roomId) {
            return {
              ...m,
              lastMessage: {
                _id: temp_id,
                content: message.content,
                senderId: this.currentUserId,
                username: "Administration",
                timestamp: new Date().toString().substring(16, 21),
                saved: false,
                seen: true,
                deleted: false,
              },
            };
          }
          return m;
        });

        // Upload regular files and videos in parallel then emit
        try {
          const toFile = (f) => {
            const raw = f.blob.originFileObj || f.blob;
            const ext = f.extension || raw.type.split("/")[1];
            return new File([raw], `${f.name}.${ext}`, { type: raw.type });
          };

          const progressBar = { value: 0 };
          const progressInterval =
            regularFiles.length > 0
              ? setInterval(() => {
                  this.updateFileProgress(temp_id, progressBar.value);
                  if (progressBar.value >= 100) clearInterval(progressInterval);
                }, 100)
              : null;

          const videoProgressBar = { value: 0 };
          const videoProgressInterval =
            videoFiles.length > 0
              ? setInterval(() => {
                  this.updateVideoUploadProgress(temp_id, videoProgressBar.value);
                }, 100)
              : null;

          const [uploadedFileKeys, uploadedVideoKeys] = await Promise.all([
            regularFiles.length > 0
              ? uploadFilesToS3(regularFiles.map(toFile), "manager/chat", progressBar)
              : Promise.resolve([]),
            videoFiles.length > 0
              ? uploadFilesToS3(
                  videoFiles.map(toFile),
                  "manager/chat/videos",
                  videoProgressBar
                )
              : Promise.resolve([]),
          ]);

          if (progressInterval) {
            clearInterval(progressInterval);
            this.markFilesAsUploaded(temp_id);
          }
          if (videoProgressInterval) {
            clearInterval(videoProgressInterval);
            this.updateVideoUploadProgress(temp_id, 100);
          }

          const afterUpload = [...this.messages];
          const msgAfterUpload = afterUpload.find(
            (m) => m._id === temp_id || m.messageId === temp_id
          );
          if (
            msgAfterUpload &&
            msgAfterUpload.files &&
            uploadedVideoKeys.length > 0
          ) {
            let vKeyIdx = 0;
            msgAfterUpload.files = msgAfterUpload.files.map((f) => {
              if (!f.isChatVideo || f.videoData?.status !== "UPLOADING") {
                return f;
              }
              const key = uploadedVideoKeys[vKeyIdx++];
              if (!key) return f;
              const fullUrl = this.settings.resourcesUrl.replace(
                "FILE_PATH",
                key
              );
              if (f.localBlobUrl) {
                try {
                  URL.revokeObjectURL(f.localBlobUrl);
                } catch (e) {
                  /* ignore */
                }
              }
              const baseLabel = (f.name || "video")
                .replace(/\s*\(uploading[^)]*\)\s*/i, "")
                .replace(/\.[^.]+$/, "");
              const fileExt = (f.extension || "mp4").toLowerCase();
              const fileType = (f.type || "video/mp4").toLowerCase();
              return {
                ...f,
                localBlobUrl: undefined,
                name: `${baseLabel} (processing…).${fileExt}`,
                type: fileType,
                extension: fileExt,
                url: fullUrl,
                preview: fullUrl,
                videoData: {
                  hlsUrl: null,
                  thumbnailUrl: null,
                  status: "PROCESSING",
                  originalKey: key,
                  renditions: [],
                  uploadProgress: 100,
                },
              };
            });
            this.messages = afterUpload;
          }

          this.$socket.client.emit("adminMessage", {
            message: message.content,
            parentId: familyId,
            files: uploadedFileKeys,
            videos: uploadedVideoKeys.map((key) => ({ originalKey: key })),
            temp_id,
            parentType,
            loginId: loginId || null,
          });
        } catch (err) {
          console.error(err);
          const failed = this.messages.find((msg) => msg._id === temp_id);
          if (failed && failed.files) {
            failed.files.forEach((f) => {
              if (f.localBlobUrl) {
                try {
                  URL.revokeObjectURL(f.localBlobUrl);
                } catch (e) {
                  /* ignore */
                }
              }
            });
          }
          this.messages = this.messages.filter((msg) => msg._id !== temp_id);
          this.$message.error(this.$t("error.fileUploadError"));
        }
      }, 50);
    },

    mapVideosToFiles(videos) {
      if (!videos || !videos.length) return [];
      return videos.map((rawVideo, i) => {
        const video =
          typeof rawVideo === "string" ? { originalKey: rawVideo } : rawVideo;
        const hlsUrl = video.hlsMasterKey
          ? this.settings.resourcesUrl.replace("FILE_PATH", video.hlsMasterKey)
          : null;
        const thumbnailUrl = video.thumbnailKey
          ? this.settings.resourcesUrl.replace("FILE_PATH", video.thumbnailKey)
          : null;
        const originalUrl = video.originalKey
          ? this.settings.resourcesUrl.replace("FILE_PATH", video.originalKey)
          : "";
        const label =
          video.status === "READY"
            ? `video-${i + 1}`
            : video.status === "FAILED"
            ? `video-${i + 1} (failed)`
            : `video-${i + 1} (processing…)`;
        const VIDEO_PLACEHOLDER = "/resources/images/video.png";
        const playableUrl = hlsUrl || originalUrl;
        const hasPlayableSource = Boolean(playableUrl);
        return {
          name: `${label}.mp4`,
          type: hasPlayableSource ? "video/mp4" : "png",
          extension: hasPlayableSource ? "mp4" : "png",
          url: hasPlayableSource ? playableUrl : VIDEO_PLACEHOLDER,
          preview: thumbnailUrl || VIDEO_PLACEHOLDER,
          isChatVideo: true,
          videoData: {
            hlsUrl,
            thumbnailUrl,
            status: video.status,
            originalKey: video.originalKey,
            renditions: video.renditions || [],
            uploadProgress: 100,
          },
        };
      });
    },

    openFile({ file }) {
      const fileData = (file && file.file) || file || {};
      const maybeUrl = fileData.url || fileData.preview || "";
      const maybeName = (fileData.name || "").toLowerCase();
      const maybeType = (fileData.type || "").toLowerCase();
      const isVideoByPath = /\/manager\/chat\/videos\//.test(maybeUrl);
      const isVideoByExt =
        maybeType.includes("mp4") ||
        maybeType.includes("video") ||
        maybeName.endsWith(".mp4") ||
        maybeName.endsWith(".webm") ||
        maybeName.endsWith(".mov") ||
        maybeName.endsWith(".mkv");

      if (fileData.isChatVideo || isVideoByPath || isVideoByExt) {
        const fallbackVideoData = {
          hlsUrl: null,
          thumbnailUrl: null,
          status: "PROCESSING",
          originalKey:
            fileData.originalKey || fileData.videoData?.originalKey || null,
          renditions: [],
        };
        this.videoModal = {
          visible: true,
          video: fileData.videoData || fallbackVideoData,
        };
        return;
      }
      if (file.action == "download") window.open(fileData.url, "_blank");
    },
    updateFileProgress(messageId, progress) {
      const message = this.messages.find(
        (message) => message._id === messageId
      );

      if (!message || !message.files) return;
      message.files = message.files.map((item) => ({
        ...item, // Spread the existing properties
        progress: progress, // Update the progress property
      }));

      this.messages = [...this.messages];

      // Update CSS custom property for progress bar
      this.$nextTick(() => {
        // Find message by temp ID
        const messageElements = document.querySelectorAll(".vac-message");
        messageElements.forEach((messageElement) => {
          const messageIdAttr =
            messageElement.getAttribute("data-message-id") ||
            messageElement.getAttribute("data-id");
          if (messageIdAttr === messageId.toString()) {
            const fileElements =
              messageElement.querySelectorAll(".vac-message-file");
            fileElements.forEach((fileElement) => {
              fileElement.style.setProperty(
                "--upload-progress",
                `${progress}%`
              );
              const fileNameElement = fileElement.querySelector(
                ".vac-message-file-name"
              );
              if (fileNameElement) {
                fileNameElement.setAttribute(
                  "data-progress",
                  Math.round(progress)
                );
              }
            });
          }
        });
      });
    },

    updateVideoUploadProgress(messageId, progress) {
      const messages = [...this.messages];
      const msg = messages.find(
        (m) => m._id === messageId || m.messageId === messageId
      );
      if (!msg || !msg.files) return;
      msg.files = msg.files.map((f) => {
        if (
          !f.isChatVideo ||
          !f.videoData ||
          f.videoData.status !== "UPLOADING"
        ) {
          return f;
        }
        return {
          ...f,
          videoData: { ...f.videoData, uploadProgress: progress },
        };
      });
      this.messages = messages;
    },

    markFilesAsUploaded(messageId) {
      const message = this.messages.find(
        (message) => message._id === messageId
      );

      if (!message || !message.files) return;
      message.files = message.files.map((item) => ({
        ...item,
        progress: 100,
        uploading: false,
      }));

      this.messages = [...this.messages];

      // Remove uploading class and progress indicators
      this.$nextTick(() => {
        // Find message by temp ID
        const messageElements = document.querySelectorAll(".vac-message");
        messageElements.forEach((messageElement) => {
          const messageIdAttr =
            messageElement.getAttribute("data-message-id") ||
            messageElement.getAttribute("data-id");
          if (messageIdAttr === messageId.toString()) {
            const fileElements =
              messageElement.querySelectorAll(".vac-message-file");
            fileElements.forEach((fileElement) => {
              fileElement.classList.remove("uploading");
              fileElement.style.removeProperty("--upload-progress");
              const fileNameElement = fileElement.querySelector(
                ".vac-message-file-name"
              );
              if (fileNameElement) {
                fileNameElement.removeAttribute("data-progress");
              }
            });
          }
        });
      });
    },
    formattedFiles(files) {
      const formattedFiles = [];

      files.forEach((file) => {
        const rawFile = file.blob.originFileObj || file.blob;
        const isAudio = this.isAudioFile(file.name) || file.audio;
        const messageFile = {
          name: file.name,
          size: file.size,
          type: isAudio ? this.getAudioMimeType(file.name) : file.type,
          extension: file.extension || file.type,
          url: URL.createObjectURL(rawFile),
          preview: URL.createObjectURL(rawFile),
          localUrl: URL.createObjectURL(rawFile),
          progress: 0, // Add progress property
          uploading: true, // Add uploading state
        };

        if (isAudio || file.audio) {
          messageFile.audio = true;
          messageFile.duration = file.duration || 0;
        }

        formattedFiles.push(messageFile);
      });

      return formattedFiles;
    },
    playNotification() {
      const audio = new Audio("/resources/notification.mp3");
      audio.play();
    },

    verifyAccess(accessRoles) {
      const existing = this.user.access;
      if (existing.all) return true;
      if (
        existing.chat &&
        existing.chat.length > 0 &&
        existing.chat.find((e) => accessRoles.includes(e))
      )
        return true;
      else return false;
    },
    addNewParent() {
      this.visibleAddParnet = true;
    },
    cancelAddParent() {
      this.visibleAddParnet = false;
      this.selectedParents = [];
      this.searchQuery = "";
      this.collapsedFamilies = [];
    },
    toggleParentSelection(parentId, parentType, parentData) {
      // Check if parent can be selected
      if (
        !parentData ||
        !parentData.login ||
        this.checkIfParentAlreadyAdded(parentId, parentType) ||
        parentData.status === "inactive"
      ) {
        return;
      }

      const selectionKey = `${parentId}-${parentType}`;
      const existingIndex = this.selectedParents.findIndex(
        (p) => p.key === selectionKey
      );

      if (existingIndex >= 0) {
        // Remove from selection
        this.selectedParents.splice(existingIndex, 1);
      } else {
        // Add to selection
        this.selectedParents.push({
          key: selectionKey,
          parentId,
          parentType,
          parentData,
        });
      }
    },
    isParentSelected(parentId, parentType) {
      const selectionKey = `${parentId}-${parentType}`;
      return this.selectedParents.some((p) => p.key === selectionKey);
    },

    getFamilyName(parent) {
      const names = [];
      if (parent.father && parent.father.firstName) {
        names.push(`${parent.father.firstName} ${parent.father.lastName}`);
      }
      if (parent.mother && parent.mother.firstName) {
        names.push(`${parent.mother.firstName} ${parent.mother.lastName}`);
      }
      if (parent.guardian && parent.guardian.firstName) {
        names.push(`${parent.guardian.firstName} ${parent.guardian.lastName}`);
      }
      return names.length > 0 ? names.join(" / ") : "N/A";
    },

    toggleFamilyCollapse(familyId) {
      const index = this.collapsedFamilies.indexOf(familyId);
      if (index > -1) {
        this.collapsedFamilies.splice(index, 1);
      } else {
        this.collapsedFamilies.push(familyId);
      }
    },

    handleAddParents() {
      if (this.selectedParents.length === 0) return;

      const newRooms = [];

      this.selectedParents.forEach(({ parentId, parentType, parentData }) => {
        const record = this.parentList.find((p) => p._id == parentId);

        // Use individual parent's photo if available, otherwise fall back to family photo
        const photo = parentData.photo
          ? this.settings.resourcesUrl.replace("FILE_PATH", parentData.photo)
          : record.photo
          ? this.settings.resourcesUrl.replace("FILE_PATH", record.photo)
          : "-";

        const roomId = `${record._id}-${parentType}`;
        const name = `${parentData.firstName} ${
          parentData.lastName
        } (${this.getParentTypeTranslation(parentType)})`;

        // Check if room already exists
        if (!this.rooms.find((r) => r.roomId == roomId)) {
          const room = {
            roomId: roomId,
            roomName: name,
            unreadCount: 0,
            avatar: photo,
            users: [
              {
                _id: "0",
                username: "Adminstration",
                status: {
                  state: "online",
                  lastChanged: "today, 14:30",
                },
              },
              {
                _id: roomId,
                username: name,
                status: {
                  state: "offline",
                },
                loginId: parentData.login,
                parentType: parentType,
                familyId: record._id,
              },
            ],
          };

          newRooms.push(room);
        }
      });

      // Add all new rooms to the beginning of the rooms array
      if (newRooms.length > 0) {
        this.rooms = [...newRooms, ...this.rooms];
        this.refreshConnectionStatus();
      }

      this.cancelAddParent();
    },

    checkIfParentAlreadyAdded(parentId, type) {
      // Check if any room exists for this parent family
      return this.rooms.find(
        (r) =>
          r.roomId.startsWith(parentId + "-") && r.users[1].parentType === type
      );
    },

    refreshConnectionStatus() {
      console.log(this.settings.activeBuilding);
      this.$socket.client.emit("connectedStatus", {
        token: tokenStorage.get(), // Get from in-memory storage
        db: this.settings.activeBuilding,
      });
    },
    isParentOnline(loginId) {
      return this.connectedParents.includes(loginId);
    },
    isTeacherOnline(teacherId) {
      return this.connectedTeachers.includes(teacherId);
    },
    updateRoomsOnlineStatus() {
      if (!this.rooms) return;
      this.rooms = this.rooms.map((room) => {
        const parentUser = room.users.find((user) => user._id !== "0");
        if (parentUser) {
          const isOnline = this.connectedParents.includes(parentUser.loginId);
          parentUser.status.state = isOnline ? "online" : "offline";
        }
        return room;
      });
    },
    getParentIdFromRoomId(roomId) {
      // Extract parent ID from roomId format: "parentId-parentType"
      const parts = roomId.split("-");
      return parts[0];
    },

    // Audio file detection function
    isAudioFile(url) {
      if (!url || typeof url !== "string") return false;
      const ext = url.split(".").pop()?.toLowerCase();
      return ["mp3", "aac"].includes(ext || "");
    },

    // Get MIME type from file extension
    getAudioMimeType(filename) {
      if (!filename || typeof filename !== "string") return "audio/mpeg";
      const ext = filename.split(".").pop()?.toLowerCase();
      const mimeTypes = {
        mp3: "audio/mpeg",
        aac: "audio/mp3", // Use audio/mp3 for better browser compatibility
      };
      return mimeTypes[ext || ""] || "audio/mpeg";
    },
  },
};
</script>

<style scoped>
.parent-selection-container {
  max-height: 500px;
  overflow-y: auto;
  padding: 8px;
}

.search-container {
  margin-bottom: 16px;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
  padding: 8px 0;
}

.parent-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.parent-family-item {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  overflow: hidden;
}

.parent-family-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.family-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  cursor: pointer;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;
}

.family-header:hover {
  background: #f0f0f0;
}

.family-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.family-name {
  font-weight: 600;
  font-size: 14px;
  color: #262626;
}

.family-actions {
  flex-shrink: 0;
}

.collapse-icon {
  font-size: 16px;
  color: #8c8c8c;
  transition: transform 0.2s ease;
}

.family-header:hover .collapse-icon {
  color: #1890ff;
}

.parent-members {
  padding: 12px;
  background: white;
}

.parent-member-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 8px;
}

.parent-member-item:last-child {
  margin-bottom: 0;
}

.parent-member-item:hover:not(.disabled) {
  border-color: #1890ff;
  background: #f8fbff;
  transform: translateY(-1px);
}

.parent-member-item.disabled {
  cursor: not-allowed;
  opacity: 0.5;
  background: #fafafa;
}

.parent-member-item.inactive {
  border-color: #ff4d4f;
  background: #fff2f0;
}

.member-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.member-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.member-name {
  font-weight: 500;
  color: #262626;
  font-size: 13px;
}

.member-type {
  color: #8c8c8c;
  font-size: 11px;
  font-weight: 400;
}

.member-status {
  display: flex;
  align-items: center;
}

.status-icon {
  font-size: 18px;
}

.status-icon.inactive {
  color: #ff4d4f;
}

.status-icon.added {
  color: #52c41a;
}

.status-icon.available {
  color: #1890ff;
}

/* Selected state */
.parent-member-item.selected {
  border-color: #1890ff;
  background: #e6f7ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
}

.parent-member-item.selected .member-name {
  color: #1890ff;
}

/* Scrollbar styling */
.parent-selection-container::-webkit-scrollbar {
  width: 6px;
}

.parent-selection-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.parent-selection-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.parent-selection-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.no-results {
  margin-top: 20px;
  text-align: center;
  padding: 40px 20px;
}

/* File upload progress indicator */
.file-upload-progress {
  position: relative;
  display: inline-block;
  width: 100%;
  margin-top: 4px;
}

.file-upload-progress .progress-bar {
  width: 100%;
  height: 4px;
  background-color: #f0f0f0;
  border-radius: 2px;
  overflow: hidden;
}

.file-upload-progress .progress-fill {
  height: 100%;
  background-color: #1890ff;
  transition: width 0.3s ease;
  border-radius: 2px;
}

.file-upload-progress .progress-text {
  font-size: 11px;
  color: #8c8c8c;
  margin-top: 2px;
  text-align: center;
}

/* Override vue-advanced-chat file display to show progress */
:deep(.vac-message-file) {
  position: relative;
}

:deep(.vac-message-file.uploading) {
  opacity: 0.8;
}

:deep(.vac-message-file.uploading .vac-message-file-container) {
  pointer-events: none;
}

/* Add progress indicator after file container */
:deep(.vac-message-file.uploading .vac-message-file-container::after) {
  content: "";
  position: absolute;
  bottom: -8px;
  left: 0;
  right: 0;
  height: 4px;
  background-color: #f0f0f0;
  border-radius: 2px;
  overflow: hidden;
}

:deep(.vac-message-file.uploading .vac-message-file-container::before) {
  content: "";
  position: absolute;
  bottom: -8px;
  left: 0;
  height: 4px;
  background-color: #1890ff;
  border-radius: 2px;
  transition: width 0.3s ease;
  width: var(--upload-progress, 0%);
}

/* Progress text */
:deep(.vac-message-file.uploading
    .vac-message-file-container
    .vac-message-file-name::after) {
  content: "Uploading... " attr(data-progress) "%";
  display: block;
  font-size: 11px;
  color: #8c8c8c;
  margin-top: 4px;
}

/* Alternative selectors in case the above don't work */
:deep(.vac-message-file.uploading)::after {
  content: "";
  position: absolute;
  bottom: -8px;
  left: 0;
  right: 0;
  height: 4px;
  background-color: #f0f0f0;
  border-radius: 2px;
  overflow: hidden;
}

:deep(.vac-message-file.uploading)::before {
  content: "";
  position: absolute;
  bottom: -8px;
  left: 0;
  height: 4px;
  background-color: #1890ff;
  border-radius: 2px;
  transition: width 0.3s ease;
  width: var(--upload-progress, 0%);
}
</style>
